import { useDispatch, useSelector } from 'react-redux';

import { cloneDeep } from 'es-toolkit';

import {
  useRentalManagerUserPropertyPhotoWebApiHandler_caption,
  useRentalManagerUserPropertyPhotoWebApiHandler_delete,
  useRentalManagerUserPropertyPhotoWebApiHandler_rank,
} from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

import PropertiesActions from '../../actions/PropertiesActions';
import { PropertiesSelectors } from '../../selectors';

import type { Dispatch, SetStateAction } from 'react';
import type { PhotoProps } from '@zg-rentals/maintenance-utils';

type DiscriminatoryContentError = { photoId: string; caption: string };

export const applyLocalStateAfterPhotoDelete = (
  photos: Array<PhotoProps>,
  discriminatoryContentErrors: Array<DiscriminatoryContentError>,
  photoId: string,
  setDiscriminatoryContentErrors: Dispatch<SetStateAction<Array<DiscriminatoryContentError>>>,
  dispatch: Dispatch<Dispatch<typeof PropertiesActions.updateCurrentProperty>>,
) => {
  const indexOfDeletedPhoto = photos.findIndex((photo: PhotoProps) => photo.id === photoId);

  // guard against deleting a photo that doesn't exist.
  if (indexOfDeletedPhoto === -1) {
    return;
  }

  const photosCopy = cloneDeep(photos);
  photosCopy.splice(indexOfDeletedPhoto, 1);

  const copyDiscriminatoryContent = cloneDeep(discriminatoryContentErrors);
  const errorIndexToUpdate = copyDiscriminatoryContent.findIndex((error) => error.photoId === photoId);
  copyDiscriminatoryContent.splice(errorIndexToUpdate, 1);

  setDiscriminatoryContentErrors(copyDiscriminatoryContent);
  dispatch(PropertiesActions.updateCurrentProperty({ photos: photosCopy }));
};

const useDeletePhoto = (
  discriminatoryContentErrors: Array<DiscriminatoryContentError>,
  setDiscriminatoryContentErrors: Dispatch<SetStateAction<Array<DiscriminatoryContentError>>>,
) => {
  const dispatch = useDispatch();
  const { photos } = useSelector((state) => PropertiesSelectors.makeCurrentProperty(state));

  const { mutate, mutateAsync, ...rest } = useRentalManagerUserPropertyPhotoWebApiHandler_delete();

  const wrappedMutate = (photoId: string, options?: Parameters<typeof mutate>[1]) =>
    mutate(
      { getParams: { photoId } },
      {
        ...options,
        onSuccess: (...args) => {
          applyLocalStateAfterPhotoDelete(
            photos,
            discriminatoryContentErrors,
            photoId,
            setDiscriminatoryContentErrors,
            dispatch,
          );

          options?.onSuccess?.(...args);
        },
      },
    );

  const wrappedMutateAsync = async (photoId: string, options?: Parameters<typeof mutateAsync>[1]) => {
    const result = await mutateAsync({ getParams: { photoId } }, options);

    applyLocalStateAfterPhotoDelete(
      photos,
      discriminatoryContentErrors,
      photoId,
      setDiscriminatoryContentErrors,
      dispatch,
    );

    return result;
  };

  return { ...rest, mutate: wrappedMutate, mutateAsync: wrappedMutateAsync };
};

const useSortPhoto = (listingAlias: string) => {
  const { mutate, mutateAsync, ...rest } = useRentalManagerUserPropertyPhotoWebApiHandler_rank();

  const wrappedMutate = (photosArray: Array<PhotoProps>, options?: Parameters<typeof mutate>[1]) =>
    mutate(
      {
        getParams: { listingAlias },
        postData: { ranks: photosArray.map((p) => ({ id: p.id, rank: String(p.rank) })) },
      },
      options,
    );

  const wrappedMutateAsync = (photosArray: Array<PhotoProps>, options?: Parameters<typeof mutateAsync>[1]) =>
    mutateAsync(
      {
        getParams: { listingAlias },
        postData: { ranks: photosArray.map((p) => ({ id: p.id, rank: String(p.rank) })) },
      },
      options,
    );

  return { ...rest, mutate: wrappedMutate, mutateAsync: wrappedMutateAsync };
};

const applyLocalStateAfterPhotoCaptionUpdate = (
  photos: Array<PhotoProps>,
  photoData: { photoId: string; caption: string },
  dispatch: Dispatch<Dispatch<typeof PropertiesActions.updateCurrentProperty>>,
) => {
  const indexOfPhotoCaption = photos.findIndex((photo: PhotoProps) => photo.id === photoData.photoId);
  const photosCopy = cloneDeep(photos);
  photosCopy[indexOfPhotoCaption].caption = photoData.caption;

  dispatch(PropertiesActions.updateCurrentProperty({ photos: photosCopy }));
};

const useUpdatePhotoCaption = () => {
  const dispatch = useDispatch();
  const { photos } = useSelector((state) => PropertiesSelectors.makeCurrentProperty(state));

  const { mutate, mutateAsync, ...rest } = useRentalManagerUserPropertyPhotoWebApiHandler_caption();

  const wrappedMutate = (photoData: { photoId: string; caption: string }, options?: Parameters<typeof mutate>[1]) =>
    mutate(
      { getParams: { photoId: photoData.photoId, caption: photoData.caption } },
      {
        ...options,
        onSuccess: (...args) => {
          applyLocalStateAfterPhotoCaptionUpdate(photos, photoData, dispatch);

          options?.onSuccess?.(...args);
        },
      },
    );

  const wrappedMutateAsync = async (
    photoData: { photoId: string; caption: string },
    options?: Parameters<typeof mutateAsync>[1],
  ) => {
    const result = await mutateAsync(
      { getParams: { photoId: photoData.photoId, caption: photoData.caption } },
      options,
    );

    applyLocalStateAfterPhotoCaptionUpdate(photos, photoData, dispatch);

    return result;
  };

  return { ...rest, mutate: wrappedMutate, mutateAsync: wrappedMutateAsync };
};

export { useDeletePhoto, useSortPhoto, useUpdatePhotoCaption };
